import { apiClient, API_BASE_URL } from './apiClient';

/**
 * OAuth Service - Frontend Authentication Layer
 * Handles Google OAuth 2.0 flow via backend API
 * 
 * SECURITY NOTE: Tokens are stored in localStorage with base64 encoding as a placeholder.
 * In production, the backend should use HttpOnly cookies for secure token management.
 */

const REFRESH_TOKEN_KEY = 'quantum_leap_refresh_token';
const ACCESS_TOKEN_KEY = 'quantum_leap_access_token';
const TOKEN_EXPIRY_KEY = 'quantum_leap_token_expiry';
const securelyStore = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, btoa(value));
  } catch (e) {
    console.error(`Failed to securely store item ${key}`, e);
  }
};

const retrieveAndDecode = (key: string): string | null => {
  try {
    const storedValue = localStorage.getItem(key);
    return storedValue ? atob(storedValue) : null;
  } catch (e) {
    console.error(`Failed to retrieve or decode item ${key}`, e);
    // Clear potentially corrupted data
    localStorage.removeItem(key);
    return null;
  }
};


// Simulate a short-lived access token (10 minutes)
const ACCESS_TOKEN_LIFETIME_MS = 10 * 60 * 1000;

/**
 * Initiates OAuth sign-in flow
 * In production, this should redirect to backend OAuth endpoint
 */
export const signIn = async (): Promise<boolean> => {
  try {
    console.log('Initiating Google OAuth 2.0 flow...');
    
    // In production, redirect to backend OAuth endpoint:
    // window.location.href = `${API_BASE_URL}/auth/google`;
    // Backend will handle OAuth flow and redirect back with tokens
    
    // For development/demo, simulate OAuth flow
    const response = await apiClient.post<{
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    }>('/auth/signin');

    const expiry = Date.now() + (response.expiresIn || ACCESS_TOKEN_LIFETIME_MS);

    securelyStore(REFRESH_TOKEN_KEY, response.refreshToken);
    securelyStore(ACCESS_TOKEN_KEY, response.accessToken);
    securelyStore(TOKEN_EXPIRY_KEY, expiry.toString());

    console.log('OAuth flow successful. Tokens stored.');
    return true;
  } catch (error) {
    console.error('OAuth sign-in failed:', error);
    return false;
  }
};

export const signOut = (): void => {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  console.log('User signed out. Tokens cleared.');
  
  // Optionally notify backend to invalidate tokens
  apiClient.post('/auth/signout').catch(err => {
    console.error('Error notifying backend of sign out:', err);
  });
};

export const isAuthenticated = (): boolean => {
  return !!retrieveAndDecode(REFRESH_TOKEN_KEY);
};

/**
 * Refreshes the access token using the refresh token via backend API
 */
const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = retrieveAndDecode(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    console.error('No refresh token available to refresh access token.');
    signOut(); // Force sign out if refresh token is missing
    return null;
  }
  
  try {
    console.log('Access token expired. Refreshing using refresh token...');
    
    const response = await apiClient.post<{
      accessToken: string;
      expiresIn: number;
    }>('/auth/refresh', { refreshToken });
    
    const newExpiry = Date.now() + (response.expiresIn || ACCESS_TOKEN_LIFETIME_MS);
    
    securelyStore(ACCESS_TOKEN_KEY, response.accessToken);
    securelyStore(TOKEN_EXPIRY_KEY, newExpiry.toString());
    
    console.log('New access token obtained.');
    return response.accessToken;
  } catch (error) {
    console.error('Failed to refresh access token:', error);
    signOut(); // Force sign out on refresh failure
    return null;
  }
};

export const getAccessToken = async (): Promise<string | null> => {
  const accessToken = retrieveAndDecode(ACCESS_TOKEN_KEY);
  const expiryStr = retrieveAndDecode(TOKEN_EXPIRY_KEY);

  if (!accessToken || !expiryStr) {
    // If we have a refresh token but no access token, refresh it.
    if (isAuthenticated()) {
      return refreshAccessToken();
    }
    return null;
  }

  const expiry = parseInt(expiryStr, 10);
  if (Number.isNaN(expiry) || Date.now() >= expiry) {
    return refreshAccessToken();
  }

  return accessToken;
};
